import type {
  FlightCardData,
  PriceConditions,
  PriceConditionsRuleDebug,
  PriceJourney,
  PriceLuggage,
  PriceOfferResponse,
  PricePassengerGroup,
  TransformedPriceResponse,
} from '@massilia/queries'

import { usePriceOfferQuery } from '@massilia/queries'
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  useIsMobile,
} from '@massilia/ui'
import { AlertTriangle, Briefcase, Luggage } from 'lucide-react'

import { SessionExpiredError } from '@/components/booking/session-expired-error'
import { TicketConditions } from '@/components/booking/shared-trip-components'
import { formatDuration } from '@/utils/format-duration'
import { PASSENGER_TYPE_LABELS_PLURAL as PASSENGER_TYPE_LABELS } from '@/utils/passenger-labels'
import { JOURNEY_TYPE_LABELS } from '@/utils/trip-labels'

import { useDebug } from './debug-context'
import { StopsIndicator } from './stops-indicator'

// ============================================================================
// Types
// ============================================================================

interface FlightConditionsModalProps {
  flight: FlightCardData
  isOpen: boolean
  onClose: () => void
  onSelectFlight?: () => void
  selectedJourneyKeys: string[]
  transactionKey: string
}

// ============================================================================
// Main Component
// ============================================================================

export function FlightConditionsModal({
  flight,
  isOpen,
  onClose,
  onSelectFlight,
  selectedJourneyKeys,
  transactionKey,
}: FlightConditionsModalProps) {
  const isMobile = useIsMobile()

  const { data, error, isPending } = usePriceOfferQuery(
    {
      journeyKeyArray: selectedJourneyKeys,
      offerKey: flight.offerKey,
      transactionKey,
    },
    { enabled: isOpen }
  )

  const showFooter = !!data && !data.error

  // Mobile → Dialog (modal)
  if (isMobile) {
    return (
      <Dialog onOpenChange={onClose} open={isOpen}>
        <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="border-gray-light-200 shrink-0 border-b px-6 py-4">
            <DialogTitle className="text-gray-light-900 text-lg leading-5.5 font-semibold">
              Détails des billets
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-6">
            <FlightConditionsContent data={data} error={error} isPending={isPending} />
          </div>
          {showFooter && <StickyFooter onClose={onClose} onSelectFlight={onSelectFlight} />}
        </DialogContent>
      </Dialog>
    )
  }

  // Desktop → Sheet (right side)
  return (
    <Sheet onOpenChange={onClose} open={isOpen}>
      <SheetContent
        className="flex w-[50vw] max-w-[50vw] flex-col gap-0 overflow-hidden p-0"
        side="right"
      >
        <SheetHeader className="border-gray-light-200 shrink-0 border-b px-6 py-4">
          <SheetTitle className="text-gray-light-900 text-lg leading-5.5 font-semibold">
            Détails des billets
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6">
          <FlightConditionsContent data={data} error={error} isPending={isPending} />
        </div>
        {showFooter && <StickyFooter onClose={onClose} onSelectFlight={onSelectFlight} />}
      </SheetContent>
    </Sheet>
  )
}

// ============================================================================
// Content
// ============================================================================

function ConditionsSection({
  conditions,
  debugRule,
}: {
  conditions: PriceConditions
  debugRule?: PriceConditionsRuleDebug
}) {
  const showDebug = useDebug()

  return (
    <div className="border-gray-light-200 flex flex-col gap-4 rounded-xl border px-4 py-6">
      <h3 className="text-gray-light-900 text-sm leading-4 font-semibold">Conditions par billet</h3>
      <TicketConditions conditions={conditions} debugRule={showDebug ? debugRule : undefined} />
    </div>
  )
}

// ============================================================================
// Passengers Section
// ============================================================================

function FlightConditionsContent({
  data,
  error,
  isPending,
}: {
  data: PriceOfferResponse | undefined
  error: Error | null
  isPending: boolean
}) {
  if (isPending) return <LoadingState />
  if (error) return <NetworkErrorState message={error.message} />
  if (!data) return null
  if (data.error)
    return (
      <SessionExpiredError
        message={data.message}
        onAction={() => {
          window.location.reload()
        }}
      />
    )
  return <PriceDetailsContent data={data.data} />
}

// ============================================================================
// Luggage Section
// ============================================================================

function JourneyCard({ journey }: { journey: PriceJourney }) {
  const firstSegment = journey.segments[0]
  const lastSegment = journey.segments[journey.segments.length - 1]

  if (!firstSegment || !lastSegment) return null

  const durationStr = formatDuration(journey.totalDuration)

  return (
    <div className="border-gray-light-200 flex flex-col items-center gap-5 rounded-xl border p-6">
      {/* Header: journey type + date, flight number, carrier logo */}
      <div className="flex w-full items-start justify-between">
        <div className="flex flex-1 flex-col gap-1">
          <span className="text-gray-light-900 text-sm leading-4 font-semibold">
            {JOURNEY_TYPE_LABELS[journey.type]} : {journey.date}
          </span>
          <span className="text-gray-light-600 text-[10px] leading-3 font-normal">
            {journey.flightNumber}
          </span>
        </div>
        {journey.carrier.logoUrl && (
          <img
            alt={journey.carrier.name}
            className="h-2.5 shrink-0 object-contain"
            src={journey.carrier.logoUrl}
          />
        )}
      </div>

      {/* Center-aligned vertical flight timeline */}
      <div className="flex flex-col items-center gap-2">
        {/* Departure */}
        <div className="flex flex-col items-center">
          <span className="text-gray-light-900 text-base leading-5 font-semibold">
            {firstSegment.departure.time}
          </span>
          <span className="text-gray-light-700 text-sm leading-4 font-medium">
            {firstSegment.departure.airport}
          </span>
        </div>

        {/* Vertical line */}
        <div className="bg-gray-light-300 h-5 w-px" />

        {/* Stops + duration */}
        <div className="flex flex-col items-center gap-1">
          <StopsIndicator legs={journey.legs} stops={journey.stops} variant="compact" />
          <span className="text-gray-light-700 text-xs leading-4 font-medium">{durationStr}</span>
        </div>

        {/* Vertical line */}
        <div className="bg-gray-light-300 h-5 w-px" />

        {/* Arrival */}
        <div className="flex flex-col items-center">
          <span className="text-gray-light-900 text-base leading-5 font-semibold">
            {lastSegment.arrival.time}
            {lastSegment.isNextDay && (
              <span className="ml-0.5 text-xs font-normal text-red-600">+1</span>
            )}
          </span>
          <span className="text-gray-light-700 text-sm leading-4 font-medium">
            {lastSegment.arrival.airport}
          </span>
        </div>
      </div>

      {/* Arrival date if next day */}
      {lastSegment.isNextDay && (
        <p className="text-xs leading-4 font-normal text-red-600">
          Arrivée le {lastSegment.arrival.date} à {lastSegment.arrival.time}
        </p>
      )}
    </div>
  )
}

// ============================================================================
// Journey Card
// ============================================================================

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="flex flex-col items-center gap-4">
        <div className="border-brand-500 size-10 animate-spin rounded-full border-4 border-t-transparent" />
        <p className="text-gray-light-600 text-sm">Chargement des détails...</p>
      </div>
    </div>
  )
}

// ============================================================================
// Conditions Section
// ============================================================================

function LuggageSection({ luggage }: { luggage: PriceLuggage }) {
  return (
    <div className="border-gray-light-200 flex flex-col gap-5 rounded-xl border p-6">
      <h3 className="text-gray-light-900 text-sm leading-4 font-semibold">
        Franchise bagage par passager
      </h3>

      {/* Checked bags */}
      <div className="flex items-center gap-3.5">
        <Luggage className="text-gray-light-600 size-4 shrink-0" />
        <div className="flex flex-col">
          {luggage.checkedBags.included ? (
            <>
              <span className="text-gray-light-700 text-xs leading-4 font-medium">
                {luggage.checkedBags.quantity}{' '}
                {luggage.checkedBags.quantity > 1 ? 'bagages' : 'bagage'} en soute
                {luggage.checkedBags.weightKgPerBag &&
                  ` (${luggage.checkedBags.weightKgPerBag} kg/bagage)`}
              </span>
              {luggage.checkedBags.description && (
                <span className="text-gray-light-600 text-[10px] leading-3 font-normal">
                  Total : {luggage.checkedBags.description}
                </span>
              )}
            </>
          ) : (
            <span className="text-gray-light-700 text-xs leading-4 font-medium">
              Aucun bagage en soute inclus
            </span>
          )}
        </div>
      </div>

      {/* Cabin bag */}
      <div className="flex items-center gap-3.5">
        <Briefcase className="text-gray-light-600 size-4 shrink-0" />
        <div className="flex flex-col">
          <span className="text-gray-light-700 text-xs leading-4 font-medium">
            {luggage.cabinBag.included ? (
              <>
                {luggage.cabinBag.quantity
                  ? `${luggage.cabinBag.quantity} bagage${luggage.cabinBag.quantity > 1 ? 's' : ''} cabine`
                  : 'Bagage cabine inclus'}
                {luggage.cabinBag.weightKg && ` (${luggage.cabinBag.weightKg} kg)`}
              </>
            ) : (
              'Pas de bagage cabine'
            )}
          </span>
        </div>
      </div>
    </div>
  )
}

function NetworkErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-6">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 size-5 shrink-0 text-red-600" />
        <div>
          <h3 className="font-medium text-red-800">Erreur de chargement</h3>
          <p className="mt-1 text-sm text-red-700">{message}</p>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Sticky Footer
// ============================================================================

function PassengersSection({
  passengers,
  totalPrice,
}: {
  passengers: PricePassengerGroup[]
  totalPrice: { amount: string; currency: string }
}) {
  return (
    <div className="border-gray-light-200 flex flex-col gap-5 rounded-xl border p-6">
      <h3 className="text-gray-light-900 text-sm leading-4 font-semibold">
        Passagers et tarification
      </h3>
      <div className="flex flex-col gap-3.5">
        {passengers.map((passenger) => (
          <div
            className="text-gray-light-900 flex items-center justify-between text-sm leading-4 font-medium"
            key={passenger.type}
          >
            <span>{PASSENGER_TYPE_LABELS[passenger.type]}</span>
            <span>
              {passenger.count} x {Number(passenger.unitPrice)} €
            </span>
          </div>
        ))}
        <div className="flex items-center justify-between">
          <span className="text-gray-light-900 text-sm leading-4 font-medium">Total</span>
          <span className="text-brand-500 text-lg leading-3 font-bold">
            {Number(totalPrice.amount)} € TTC
          </span>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// State Components
// ============================================================================

function PriceDetailsContent({ data }: { data: TransformedPriceResponse }) {
  return (
    <div className="flex flex-col gap-4">
      {/* Top row: Passengers & Luggage */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <PassengersSection passengers={data.passengers} totalPrice={data.totalPrice} />
        <LuggageSection luggage={data.luggage} />
      </div>

      {/* Conditions */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <ConditionsSection conditions={data.conditions} debugRule={data.conditionsRuleDebug} />
      </div>

      {/* Journeys */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {data.journeys.map((journey, index) => (
          <JourneyCard journey={journey} key={`${journey.type}-${index}`} />
        ))}
      </div>
    </div>
  )
}

function StickyFooter({
  onClose,
  onSelectFlight,
}: {
  onClose: () => void
  onSelectFlight?: () => void
}) {
  return (
    <div className="border-gray-light-200 flex shrink-0 items-center justify-end gap-3 border-t bg-white p-6">
      <Button onClick={onClose} variant="outline">
        Retour
      </Button>
      <Button
        className="bg-linear-to-b from-[#ea6c31] to-[#d84a2d] text-white shadow-none hover:from-[#d85f2a] hover:to-[#c44328]"
        onClick={() => {
          onSelectFlight?.()
          onClose()
        }}
      >
        Sélectionner ce vol
      </Button>
    </div>
  )
}
